<template>
  <v-expansion-panel>
    <v-expansion-panel-header>
      <div class="turma-info">
        <v-avatar size="40" :color="turma.is_infantil ? 'deep-purple' : 'teal darken-1'">
          <v-icon dark>{{ turma.is_infantil ? "mdi-baby-face-outline" : "mdi-school" }}</v-icon>
        </v-avatar>

        <div class="flex-grow-1">
          <div class="text-subtitle-1 font-weight-medium text-uppercase">
            {{ turma.descricao }}
          </div>
          <div class="d-flex align-center flex-wrap">
            <v-chip
              x-small
              :color="turma.is_infantil ? 'deep-purple lighten-4' : 'teal lighten-4'"
              class="mr-1 mb-1"
            >
              {{ turma.is_infantil ? "Educação Infantil" : "Ensino Fundamental" }}
            </v-chip>
            <v-chip x-small color="grey lighten-3" class="mr-1 mb-1">
              {{ turma.total_alunos }} aluno(s)
            </v-chip>
          </div>
        </div>

        <div class="d-flex flex-row align-center">
          <v-chip outlined :color="turma.desempenho_turma.cor" class="mr-2">
            <v-icon small left>{{ indiceIcon(turma.situacao_turma) }}</v-icon>
            Situação: {{ turma.situacao_turma }}
          </v-chip>
          <v-chip :color="turma.desempenho_turma.cor" text-color="white">
            <v-icon small left>mdi-chart-line</v-icon>
            Desempenho: {{ turma.desempenho_turma.percentual }}%
          </v-chip>
        </div>
      </div>
    </v-expansion-panel-header>

    <v-expansion-panel-content>
      <v-divider class="my-3"></v-divider>

      <!-- Legenda -->
      <div class="legenda mb-4">
        <span class="mr-2"><strong>Legenda:</strong></span>
        <v-chip class="mr-1 mb-1" small color="green" text-color="white">
          <v-icon small left>mdi-emoticon-happy-outline</v-icon> Bom
        </v-chip>
        <v-chip class="mr-1 mb-1" small color="yellow" text-color="black">
          <v-icon small left>mdi-emoticon-neutral-outline</v-icon> Regular
        </v-chip>
        <v-chip class="mr-1 mb-1" small color="red" text-color="white">
          <v-icon small left>mdi-emoticon-sad-outline</v-icon> Ruim
        </v-chip>
        <v-chip class="mr-1 mb-1" small color="grey lighten-2">
          <v-icon small left>mdi-clock-outline</v-icon> Etapa não iniciada
        </v-chip>

        <template v-if="turma.is_infantil && turma.bncc">
          <v-divider vertical class="mx-2"></v-divider>
          <v-chip
            v-for="(descricao, codigo) in turma.bncc.legendas"
            :key="codigo"
            class="mr-1 mb-1"
            small
            outlined
          >
            <strong>{{ codigo }}</strong
            >&nbsp;-&nbsp;{{ descricao }}
          </v-chip>
          <v-chip
            v-for="opcao in $constants.opcoesNotaAnual"
            :key="opcao.codigo"
            class="mr-1 mb-1"
            small
            outlined
            color="blue-grey"
          >
            <strong>{{ opcao.codigo }}</strong
            >&nbsp;-&nbsp;{{ opcao.descricao }}
          </v-chip>
        </template>
      </div>

      <!-- Cards informativos (Cenário 8) -->
      <v-row class="mb-2">
        <v-col cols="12" sm="6" md="4">
          <v-card outlined class="info-card">
            <v-card-title class="text-subtitle-2">
              <v-icon left color="teal darken-1">mdi-account-group</v-icon> Dados da Turma
            </v-card-title>
            <v-card-text>
              <div><strong>Turma:</strong> {{ turma.descricao }}</div>
              <div><strong>Curso:</strong> {{ turma.curso || "- - -" }}</div>
              <div class="d-flex align-center flex-wrap">
                <strong class="mr-1">Etapa(s):</strong>
                <v-chip
                  v-for="(serie, i) in turma.series"
                  :key="i"
                  x-small
                  color="teal lighten-4"
                  class="mr-1 mb-1"
                >
                  {{ serie }}
                </v-chip>
                <span v-if="!turma.series || turma.series.length === 0">- - -</span>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-card outlined class="info-card">
            <v-card-title class="text-subtitle-2">
              <v-icon left color="purple">mdi-file-document-edit</v-icon>
              {{ turma.is_infantil ? "Conceitos" : "Notas" }}
            </v-card-title>
            <v-card-text>
              <div>
                {{
                  turma.is_infantil
                    ? "Conceitos por habilidade do Sistema BNCC (S1, S2) e Nota Anual por campo."
                    : "Notas por disciplina (resultados do circuito)."
                }}
              </div>
              <div><strong>Alunos:</strong> {{ turma.total_alunos }}</div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <v-card outlined class="info-card">
            <v-card-title class="text-subtitle-2">
              <v-icon left color="orange">mdi-clipboard-check</v-icon> Frequência / Aulas
            </v-card-title>
            <v-card-text>
              <div><strong>Aulas confirmadas:</strong> {{ turma.qtd_aulas }}</div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Alunos -->
      <div
        v-for="(aluno, index) in turma.alunos"
        :key="aluno.matricula_id"
        class="aluno-card mb-4"
        :class="{ 'aluno-pcd': aluno.especial, 'zebra-odd': index % 2 === 1 }"
      >
        <div class="d-flex align-center flex-wrap pa-3 aluno-header">
          <v-avatar size="34" color="teal darken-1" class="mr-3">
            <span class="white--text text-caption">{{ iniciais(aluno.nome) }}</span>
          </v-avatar>
          <span class="text-subtitle-1 font-weight-medium text-uppercase mr-2">
            {{ aluno.nome }}
          </span>
          <v-chip
            v-if="aluno.especial"
            x-small
            color="blue darken-1"
            text-color="white"
            class="mr-2"
          >
            <v-icon x-small left>mdi-wheelchair-accessibility</v-icon> PcD
          </v-chip>
          <v-spacer></v-spacer>
          <v-chip small :color="resultadoColor(aluno.resultado)" text-color="white">
            {{ aluno.resultado }}
          </v-chip>
        </div>

        <div class="pa-3 pt-0">
          <!-- SEÇÃO: Notas (Fundamental) -->
          <div v-if="!turma.is_infantil" class="secao mb-4">
            <h5 class="secao-titulo notas">
              <v-icon left color="purple">mdi-file-document-edit</v-icon> Notas
            </h5>
            <div class="table-scroll">
              <table class="nota-grid">
                <thead>
                  <tr>
                    <th rowspan="2" class="disc-col">Disciplina</th>
                    <th
                      v-for="grupo in turma.colunas_notas"
                      :key="grupo.etapa_id"
                      :colspan="grupo.subetapas.length"
                      :class="{ 'etapa-futura': !grupo.iniciada }"
                      :title="
                        grupo.iniciada ? '' : 'Etapa ainda não iniciada — aguardando o período'
                      "
                    >
                      <v-icon v-if="!grupo.iniciada" x-small color="grey">mdi-clock-outline</v-icon>
                      {{ grupo.etapa_descricao }}
                    </th>
                  </tr>
                  <tr>
                    <template v-for="grupo in turma.colunas_notas">
                      <th v-for="sub in grupo.subetapas" :key="sub.sub_etapa_id">
                        {{ sub.descricao }}
                      </th>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="disc in aluno.disciplinas" :key="disc.disciplina_id">
                    <td class="disc-nome">{{ disc.nome }}</td>
                    <template v-for="grupo in turma.colunas_notas">
                      <td
                        v-for="sub in grupo.subetapas"
                        :key="sub.sub_etapa_id"
                        class="nota-cell"
                        :class="cellClasse(disc, sub.sub_etapa_id, grupo.iniciada)"
                      >
                        {{ cellValor(disc, sub.sub_etapa_id, grupo.iniciada) }}
                      </td>
                    </template>
                  </tr>
                  <tr v-if="!aluno.disciplinas || aluno.disciplinas.length === 0">
                    <td :colspan="totalColunas + 1" class="text-center grey--text">
                      Sem notas lançadas
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="temEtapaFutura" class="text-caption grey--text mt-2 d-flex align-center">
              <v-icon x-small color="grey" class="mr-1">mdi-clock-outline</v-icon>
              As etapas em cinza (—) ainda não chegaram no período e por isso não são exibidas nem
              consideradas no cálculo do desempenho.
            </div>
          </div>

          <!-- SEÇÃO: Conceitos BNCC (Infantil) -->
          <div v-else class="secao mb-4 mt-4">
            <h5 class="secao-titulo conceitos">
              <v-icon left color="purple">mdi-shape-outline</v-icon> Conceitos (Sistema BNCC)
            </h5>

            <div v-if="!turma.bncc || turma.bncc.campos.length === 0" class="grey--text py-2">
              Sistema BNCC não configurado para esta turma.
            </div>

            <div
              v-for="campo in turma.bncc ? turma.bncc.campos : []"
              :key="campo.id"
              class="campo-bloco mb-3"
            >
              <div class="d-flex align-center flex-wrap campo-header">
                <span class="font-weight-medium mr-2">{{ campo.descricao }}</span>
                <v-spacer></v-spacer>
                <span class="text-caption mr-1">Nota Anual:</span>
                <v-chip x-small color="blue-grey" text-color="white">
                  {{ notaAnual(aluno, campo.id) }}
                </v-chip>
              </div>

              <v-simple-table dense class="elevation-1">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Habilidade</th>
                      <th class="text-center" style="width: 90px">S1</th>
                      <th class="text-center" style="width: 90px">S2</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="hab in campo.habilidades" :key="hab.id">
                      <td>
                        <span class="font-weight-medium">{{ hab.apelido }}</span>
                        - {{ hab.descricao }}
                      </td>
                      <td class="text-center">
                        <v-chip
                          small
                          :color="indiceColor(notaHab(aluno, hab.id).indice_s1)"
                          :text-color="indiceTextColor(notaHab(aluno, hab.id).indice_s1)"
                        >
                          {{ notaHab(aluno, hab.id).nota_s1 || "- - -" }}
                        </v-chip>
                      </td>
                      <td class="text-center">
                        <v-chip
                          small
                          :color="indiceColor(notaHab(aluno, hab.id).indice_s2)"
                          :text-color="indiceTextColor(notaHab(aluno, hab.id).indice_s2)"
                        >
                          {{ notaHab(aluno, hab.id).nota_s2 || "- - -" }}
                        </v-chip>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>
          </div>

          <!-- SEÇÃO: Frequência -->
          <div class="secao">
            <h5 class="secao-titulo frequencia">
              <v-icon left color="orange">mdi-clipboard-check</v-icon> Frequência
            </h5>
            <v-simple-table dense class="elevation-1">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-center">Qtd. Aulas</th>
                    <th class="text-center">Presenças</th>
                    <th class="text-center">Faltas</th>
                    <th class="text-center">Faltas Justificadas</th>
                    <th class="text-center">Frequência</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="text-center">{{ aluno.frequencia.qtd_aulas }}</td>
                    <td class="text-center">{{ aluno.frequencia.presencas }}</td>
                    <td class="text-center">{{ aluno.frequencia.faltas }}</td>
                    <td class="text-center">{{ aluno.frequencia.faltas_justificadas }}</td>
                    <td class="text-center">
                      <v-chip
                        small
                        :color="indiceColor(aluno.frequencia.indice)"
                        :text-color="indiceTextColor(aluno.frequencia.indice)"
                      >
                        <v-icon small left>{{ indiceIcon(aluno.frequencia.indice) }}</v-icon>
                        {{ aluno.frequencia.indice }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>
      </div>

      <div v-if="!turma.alunos || turma.alunos.length === 0" class="text-center py-4 grey--text">
        <v-icon large color="grey">mdi-information</v-icon>
        <div>Nenhum aluno matriculado nesta turma.</div>
      </div>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
export default {
  name: "TurmaExpansionItem",

  props: {
    turma: {
      type: Object,
      required: true,
    },
  },

  computed: {
    totalColunas() {
      return (this.turma.colunas_notas || []).reduce(
        (total, grupo) => total + grupo.subetapas.length,
        0
      );
    },

    temEtapaFutura() {
      return (this.turma.colunas_notas || []).some((grupo) => !grupo.iniciada);
    },
  },

  methods: {
    iniciais(nome) {
      if (!nome) return "?";
      const nomes = nome.trim().split(" ");
      if (nomes.length === 1) return nomes[0].substring(0, 2).toUpperCase();
      return (nomes[0][0] + nomes[nomes.length - 1][0]).toUpperCase();
    },

    indiceColor(indice) {
      switch (indice) {
        case "Bom":
          return "green";
        case "Regular":
          return "yellow";
        case "Ruim":
          return "red";
        default:
          return "grey lighten-2";
      }
    },

    indiceTextColor(indice) {
      if (indice === "Regular") return "black";
      if (!indice) return "grey darken-1";
      return "white";
    },

    indiceIcon(indice) {
      switch (indice) {
        case "Bom":
          return "mdi-emoticon-happy-outline";
        case "Regular":
          return "mdi-emoticon-neutral-outline";
        case "Ruim":
          return "mdi-emoticon-sad-outline";
        default:
          return "mdi-help-circle-outline";
      }
    },

    resultadoColor(resultado) {
      switch (resultado) {
        case "Aprovado":
        case "APROVADO":
          return "green";
        case "Reprovado":
        case "REPROVADO":
          return "red";
        case "Cursando":
        case "CURSANDO":
        case "RECEBIDA":
          return "blue";
        default:
          return "orange";
      }
    },

    cellResultado(disc, subId) {
      return (disc.resultados && disc.resultados[subId]) || null;
    },

    cellValor(disc, subId, iniciada) {
      if (iniciada === false) return "—";
      const r = this.cellResultado(disc, subId);
      if (!r || r.valor === null || r.valor === undefined || r.valor === "") return "- - -";
      if (r.tipo_campo === "resultado_em_lote") {
        const num = Number(r.valor);
        if (!Number.isNaN(num)) return num.toFixed(1);
      }
      return r.valor;
    },

    cellClasse(disc, subId, iniciada) {
      if (iniciada === false) return "nota-futura";
      const r = this.cellResultado(disc, subId);
      if (!r || !r.indice) return "";
      return `nota-${r.indice.toLowerCase()}`;
    },

    notaHab(aluno, habilidadeId) {
      return (aluno.conceitos && aluno.conceitos.notas[habilidadeId]) || {};
    },

    notaAnual(aluno, campoId) {
      return (aluno.conceitos && aluno.conceitos.notas_anuais[campoId]) || "- - -";
    },
  },
};
</script>

<style scoped>
.turma-info {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.legenda {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.info-card {
  height: 100%;
  border-radius: 8px !important;
}

.aluno-card {
  border: 1px solid #e0e0e0;
  border-left: 4px solid #009688; /* teal */
  border-radius: 8px;
  background-color: #ffffff;
  overflow: hidden;
  position: relative;
}

.aluno-card.zebra-odd {
  background-color: #fafafa;
}

.aluno-header {
  background-color: rgba(0, 150, 136, 0.06);
}

/* Seções por tipo de dado (padrão do desempenho do professor) */
.secao-titulo {
  color: #424242;
  border-bottom: 2px solid;
  padding-bottom: 6px;
  margin-bottom: 10px;
  font-size: 1rem;
}
.secao-titulo.notas,
.secao-titulo.conceitos {
  border-color: #9c27b0;
}
.secao-titulo.frequencia {
  border-color: #ff9800;
}

/* Grid de notas do Fundamental (estilo boletim, clean) */
.table-scroll {
  overflow-x: auto;
}

.nota-grid {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  background-color: #fff;
}

.nota-grid th,
.nota-grid td {
  border: 1px solid #e0e0e0;
  padding: 4px 8px;
  text-align: center;
  white-space: nowrap;
}

.nota-grid thead th {
  background-color: #f5f5f5;
  font-weight: 600;
  color: #424242;
}

.nota-grid .disc-col,
.nota-grid .disc-nome {
  text-align: left;
}

.nota-grid .disc-nome {
  font-weight: 500;
  white-space: normal;
  min-width: 180px;
}

.nota-grid .nota-cell {
  font-weight: 600;
}

.nota-grid .nota-bom {
  background-color: #e8f5e9;
  color: #1b5e20;
}

.nota-grid .nota-regular {
  background-color: #fffde7;
  color: #f9a825;
}

.nota-grid .nota-ruim {
  background-color: #ffebee;
  color: #c62828;
}

.nota-grid .etapa-futura {
  color: #9e9e9e;
  font-style: italic;
}

.nota-grid .nota-futura {
  background-color: #fafafa;
  color: #bdbdbd;
}

.campo-bloco .campo-header {
  background-color: #f5f5f5;
  padding: 6px 8px;
  border-radius: 4px 4px 0 0;
}

/* Marca d'água azul para aluno PcD (padrão do município) */
.aluno-pcd {
  border-left-color: #1565c0 !important;
  background-color: #e3f2fd !important;
}

.aluno-pcd .aluno-header {
  background-color: rgba(21, 101, 192, 0.12);
}

.aluno-pcd::before {
  content: "PcD";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-30deg);
  font-size: 6rem;
  font-weight: 700;
  color: rgba(21, 101, 192, 0.07);
  pointer-events: none;
  z-index: 0;
  letter-spacing: 0.5rem;
}

.aluno-pcd .aluno-header,
.aluno-pcd .pa-3 {
  position: relative;
  z-index: 1;
}

@media (max-width: 600px) {
  .turma-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}
</style>
